var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { apiGetListClientMyPageRfps } from '@/actions/ClientMyPageActions/getListRfp';
import { apiGetListClientMyPageOrders } from '@/actions/ClientMyPageActions/getListSolution';
import { rfpClientMyPageProjectViewTabValues, rfpClientMyPageProjectViewRfpTabValue, } from '@/components/pages/mypage/client/ClientMyPageProjectView';
import { scrollToWithHeaderHeight } from '@/helpers/ScrollUtils';
import { useNavigate } from '@/hooks/lib/react-router/useNavigate';
import { useDidMount } from '@/hooks/lifecycle/useDidMount';
import { useUnmountRef } from '@/hooks/lifecycle/useUnmountRef';
import { useBoolState } from '@/hooks/state/useBoolState';
import { wrapDispatch } from '@/utils/actions/dispatch';
import { sleep } from '@/utils/async/sleep';
import { parseQuery, getParsedQueryString, } from '@/utils/libs/query-string/parse';
import { stringifyUrl } from '@/utils/libs/query-string/stringify';
export const getEnablePageNumber = (value) => {
    const intValue = parseInt(value, 10);
    if (!Number.isNaN(intValue) && intValue > 0) {
        return intValue;
    }
    return 1;
};
export const useClientMyPageProject = (args) => {
    const { location, match, scrollTargetId } = args;
    const unmountRef = useUnmountRef();
    const dispatch = useDispatch();
    const history = useNavigate();
    const query = parseQuery(location.search);
    const queryTab = getParsedQueryString(query.tab);
    const initialTab = rfpClientMyPageProjectViewTabValues.includes(queryTab)
        ? queryTab
        : rfpClientMyPageProjectViewRfpTabValue;
    // 表示中のタブ
    const [tab, setTab] = useState(initialTab);
    // 提案募集中の案件 表示中ページ
    const [rfpsPaging, serRfpsPaging] = useState(() => getEnablePageNumber(getParsedQueryString(query.rfpsPaging)));
    // 問い合わせ中のプロジェクト事例 表示中ページ
    const [solutionsPaging, serSolutionsPaging] = useState(getEnablePageNumber(getParsedQueryString(query.solutionsPaging)));
    // 提案募集中の案件 Loading
    const { boolState: rfpLoading, changeFalse: changeRfpNotLoading, changeTrue: changeRfpLoading, } = useBoolState();
    // 問い合わせ中のプロジェクト事例 Loading
    const { boolState: solutionsLoading, changeFalse: changeSolutionsNotLoading, changeTrue: changeSolutionsLoading, } = useBoolState();
    /**
     * 提案募集中の案件 一覧 取得
     */
    const loadRfps = useCallback((page) => __awaiter(void 0, void 0, void 0, function* () {
        unmountRef.current = false;
        changeRfpLoading();
        yield wrapDispatch(dispatch, apiGetListClientMyPageRfps({ query: { page } }, { query: { page } })).finally(() => {
            if (!unmountRef.current) {
                changeRfpNotLoading();
            }
        });
    }), [changeRfpLoading, changeRfpNotLoading, dispatch, unmountRef]);
    /**
     * 問い合わせ中のプロジェクト事例 一覧 取得
     */
    const loadSolutions = useCallback((page) => __awaiter(void 0, void 0, void 0, function* () {
        unmountRef.current = false;
        changeSolutionsLoading();
        yield wrapDispatch(dispatch, apiGetListClientMyPageOrders({ query: { page } }, { page })).finally(() => {
            if (!unmountRef.current) {
                changeSolutionsNotLoading();
            }
        });
    }), [changeSolutionsLoading, changeSolutionsNotLoading, dispatch, unmountRef]);
    /**
     * url 更新
     */
    const updateHistory = useCallback((updateValue) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const current = { rfpsPaging, solutionsPaging, tab };
        const params = Object.assign(Object.assign({}, current), updateValue);
        const newUrl = stringifyUrl({
            query: Object.assign({}, params),
            url: (_a = match === null || match === void 0 ? void 0 : match.url) !== null && _a !== void 0 ? _a : '',
        });
        // state更新
        setTab(params.tab);
        serRfpsPaging(params.rfpsPaging);
        serSolutionsPaging(params.solutionsPaging);
        if (newUrl) {
            history.replace(newUrl);
        }
        if (params.tab === rfpClientMyPageProjectViewRfpTabValue) {
            yield loadRfps(params.rfpsPaging);
        }
        else {
            yield loadSolutions(params.solutionsPaging);
        }
    }), [
        tab,
        rfpsPaging,
        solutionsPaging,
        match,
        setTab,
        serRfpsPaging,
        serSolutionsPaging,
        loadRfps,
        loadSolutions,
        history,
    ]);
    /**
     * タブクリック時のイベント
     */
    const onClickTab = useCallback((tabValue) => __awaiter(void 0, void 0, void 0, function* () {
        yield updateHistory({ tab: tabValue });
    }), [updateHistory]);
    /**
     * ページ切り替え時のイベント
     */
    const onChangePage = useCallback((arg) => __awaiter(void 0, void 0, void 0, function* () {
        const selected = typeof arg === 'number' ? arg : arg.selected;
        const page = selected + 1;
        scrollToWithHeaderHeight(scrollTargetId);
        yield sleep(0.8);
        if (tab === rfpClientMyPageProjectViewRfpTabValue) {
            yield updateHistory({ rfpsPaging: page });
        }
        else {
            yield updateHistory({ solutionsPaging: page });
        }
    }), [tab, scrollTargetId, updateHistory]);
    /**
     * useEffect: 表示中のタブのデータを読み込む
     */
    useDidMount(() => {
        if (tab === rfpClientMyPageProjectViewRfpTabValue) {
            loadRfps(rfpsPaging);
        }
        else {
            loadSolutions(solutionsPaging);
        }
    });
    return {
        onChangePage,
        onClickTab,
        rfpLoading,
        rfpsPaging,
        solutionsLoading,
        solutionsPaging,
        tab,
    };
};
